import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'redux/types';
import { pollUntil } from '../services/polling/pollUntil';
import { getCreditsResume, CreditsResumeResponse } from '../services/credits/api';
import {
  trackOnboardingCreditsPollingStarted,
  trackOnboardingCreditsPollingSucceeded,
  trackOnboardingCreditsPollingTimedOut,
  trackOnboardingCreditsPollingFailed,
} from '../services/tracking/onboarding/onboardingCreditsPollingEvents';

export const CREDITS_POLLING_TIMEOUT_MS = 90_000;
export const CREDITS_POLLING_INITIAL_INTERVAL_MS = 3_000;
export const CREDITS_POLLING_BACKOFF = { multiplier: 1.5, maxIntervalMs: 15_000 };

const RECENT_ACCOUNT_WINDOW_MS = 24 * 60 * 60 * 1000;

type CreditsSlice = {
  credits?: {
    isLoading?: boolean;
    credits?: { hasSubscription?: boolean; hasBonus?: boolean };
  };
};

type Dispatch = (action: { type: string; payload?: unknown }) => void;

function hasAccess(resume: CreditsResumeResponse): boolean {
  return resume.hasSubscription || resume.hasBonus;
}

function isRecentAccount(createdAt: string | undefined): boolean {
  if (!createdAt) {
    return false;
  }

  const createdAtMs = new Date(createdAt).getTime();

  return (
    !Number.isNaN(createdAtMs) &&
    Date.now() - createdAtMs <= RECENT_ACCOUNT_WINDOW_MS
  );
}

let sharedPollingRun: Promise<void> | null = null;

function startOrJoinPolling(dispatch: Dispatch): Promise<void> {
  if (sharedPollingRun) {
    return sharedPollingRun;
  }

  const startedAt = Date.now();
  trackOnboardingCreditsPollingStarted();

  sharedPollingRun = (async () => {
    let lastResume: CreditsResumeResponse | null = null;

    try {
      const ready = await pollUntil({
        request: async (signal) => {
          lastResume = await getCreditsResume(signal);
          return lastResume;
        },
        isReady: hasAccess,
        timeoutMs: CREDITS_POLLING_TIMEOUT_MS,
        intervalMs: CREDITS_POLLING_INITIAL_INTERVAL_MS,
        backoff: CREDITS_POLLING_BACKOFF,
      });

      const durationMs = Date.now() - startedAt;

      if (ready && lastResume) {
        dispatch({ type: 'LOAD_CREDITS_RESUME_SUCCESS', payload: lastResume });
        trackOnboardingCreditsPollingSucceeded(durationMs);
        return;
      }

      dispatch({ type: 'LOAD_CREDITS_RESUME_FAILED' });
      trackOnboardingCreditsPollingTimedOut(durationMs);
    } catch {
      dispatch({ type: 'LOAD_CREDITS_RESUME_FAILED' });
      trackOnboardingCreditsPollingFailed(Date.now() - startedAt);
    }
  })().finally(() => {
    sharedPollingRun = null;
  });

  return sharedPollingRun;
}

export function useOnboardingCreditsPolling() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const creditsState = useSelector(
    (state: RootState & CreditsSlice) => state.credits,
  );

  const isInitialLoadResolved = !(creditsState?.isLoading ?? true);
  const hasSubscription = creditsState?.credits?.hasSubscription ?? false;
  const hasBonus = creditsState?.credits?.hasBonus ?? false;

  const shouldPoll =
    isInitialLoadResolved &&
    !hasSubscription &&
    !hasBonus &&
    isRecentAccount(user?.created_at);

  const [pollAttempt, setPollAttempt] = useState<'idle' | 'running' | 'done'>(
    'idle',
  );

  if (shouldPoll && pollAttempt === 'idle') {
    setPollAttempt('running');
  }

  useEffect(() => {
    if (pollAttempt !== 'running') {
      return;
    }

    let isMounted = true;

    startOrJoinPolling(dispatch as Dispatch).finally(() => {
      if (isMounted) {
        setPollAttempt('done');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [pollAttempt, dispatch]);

  return { isPolling: pollAttempt === 'running' };
}

export function __resetOnboardingCreditsPollingStateForTests(): void {
  sharedPollingRun = null;
}
